import {Inject, Injectable, DOCUMENT} from '@angular/core';
import {CxEvent, Page, User} from '@spartacus/core';
import {BehaviorSubject, combineLatest, delay, Observable} from 'rxjs';
import {DcpDataService} from '../dcp-data.service';
import {EddlAdobeTrackingData, EddlCollectionState, EddlUserAttributes} from './models/eddl-adobe-tracking-data';
import {PageEvent, PageLayoutService, ProductDetailsPageEvent, SearchPageResultsEvent} from '@spartacus/storefront';
import {DcpAuthService} from '@app/auth/services/dcp-auth.service';
import {UntilDestroy, untilDestroyed} from '@ngneat/until-destroy';
import {distinctUntilChanged, filter, first, map, switchMap} from 'rxjs/operators';
import {DcpFormatDataService} from '../dcp-format-data.service';

import {UserAccountFacade} from '@spartacus/user/account/root';
import {NavigationStart, Router} from '@angular/router';
import {EddlAfterInitializationEvent} from './events/eddl-after-initialization-event';
import {NavigationComponentService} from '@app/services/navigation-component.service';
import {DcpActiveCartService} from '@app/services/dcp-active-cart.service';
import {EddlPageMergedAbstract} from '@app/services/analytics/EDDL/events/eddl-page-merged.abstract';
import {DcpEddlDispatchService} from './dcp-eddl-dispatch-beacon.service';
import {getIDSid} from './utils/sbn-user-check';
import {USER_ATTRIBUTES_JWT_FIELDS, USER_ROLE_DEFAULT} from './utils/user-attributes';
import {FeatureTogglesService} from '@dcp/configurator';
import {UNLEASH_TOGGLES} from '@app/config/FeatureToggleConfig';
import {SearchPageEvent} from './events/search-page-event';
import {EDDL_SEARCH_PAGEVIEW_CONFIRMED_KEY, SearchPageEventService} from './events/search-page-event.service';

const EVENT_NAME: string = 'pageView';
const SECTION_HOMEPAGE: string = 'homepage';

@UntilDestroy()
@Injectable({
  providedIn: 'root',
})
export class DcpEddlPageViewService extends DcpDataService<EddlAdobeTrackingData> {
  private lastPageEvent: PageEvent = undefined;
  private hasBeenInitializedSubject: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  private searchWebcomponentEnabled: boolean = false;

  constructor(
    @Inject(DOCUMENT) public documentObject: Document,
    protected activeCartService: DcpActiveCartService,
    protected authService: DcpAuthService,
    protected formatDataService: DcpFormatDataService,
    protected navigationService: NavigationComponentService,
    protected pageLayoutService: PageLayoutService,
    protected router: Router,
    protected userFacade: UserAccountFacade,
    protected dispatchService: DcpEddlDispatchService,
    protected featureToggles: FeatureTogglesService,
    protected searchPageEventService: SearchPageEventService
  ) {
    super();
    this.routerSubscribe();
    this.featureToggles
      .isEnabled(UNLEASH_TOGGLES.searchWebcomponentEnabled)
      .pipe(untilDestroyed(this))
      .subscribe((enabled) => (this.searchWebcomponentEnabled = enabled));
  }

  fillData(
    event: EddlPageMergedAbstract,
    data?: EddlAdobeTrackingData,
    isUserLoggedIn?: boolean,
    page?: Page,
    user?: User,
    extra?: any
  ): EddlAdobeTrackingData {
    const url: string = this.formatDataService.formatUrl(this.documentObject.defaultView.location.href);
    const detailsFromToken = this.authService.getParsedJwtToken<EddlUserAttributes>(USER_ATTRIBUTES_JWT_FIELDS);
    return {
      event: EVENT_NAME,
      page: {
        name: this.getNameFromUrl(event.navigation.url),
        detail: page.description,
        section: this.navigationService.getSectionFromUrl(event.navigation.url),
        language: 'en', // DCP-51308: hardcoded as we don't support other languages at the moment
        country: 'glo', // DCP-51308: hardcoded as we don't offer country specific sites at the moment
        url,
        template: page.template,
        referrer: sessionStorage.getItem('prevLocation') ? sessionStorage.getItem('prevLocation') : '/',
      },
      user: {
        loginStatus: isUserLoggedIn ? 'yes' : 'no',
        role: USER_ROLE_DEFAULT, // fallback for anonymous users (token spread is skipped when isUserLoggedIn=false)
        scimUserId: isUserLoggedIn && user?.scimUserId ? user.scimUserId : '',
        ...(isUserLoggedIn && user?.scimUserId ? {account: {IDSid: getIDSid(user.scimUserId)}} : {}),
        ...(isUserLoggedIn ? (detailsFromToken ?? {}) : {}),
      },
    };
  }

  getData(event: CxEvent): Observable<EddlAdobeTrackingData> | EddlAdobeTrackingData {
    if (event instanceof EddlAfterInitializationEvent) {
      this.hasBeenInitializedSubject.next(true);
    }

    if (!(event instanceof PageEvent)) {
      return undefined;
    }

    // Spartacus' native ProductPageEventBuilder still emits SearchPageResultsEvent
    // off getResults() on the search route, independent of the WC. SearchPageEventService
    // is the source of record on the WC path, so suppress the native event when the
    // toggle is on to avoid a duplicate pageView.
    if (this.searchWebcomponentEnabled && event instanceof SearchPageResultsEvent) {
      return undefined;
    }

    if (!this.filterByUrl(event)) {
      return undefined;
    }

    const currentPath = this.documentObject.defaultView?.location?.pathname;
    const lastPathSegment = currentPath?.split('/')?.slice(-1)?.[0];

    if (lastPathSegment === 'cart') {
      return combineLatest([this.hasBeenInitializedSubject, this.activeCartService.getActiveCart()]).pipe(
        untilDestroyed(this),
        distinctUntilChanged(),
        filter(([initialized, cart]) => Object.keys(cart).length > 0),
        filter(Boolean),
        switchMap(([initialized, cart]) =>
          combineLatest([this.authService.isUserLoggedIn(), this.pageLayoutService.page$, this.userFacade.get()]).pipe(
            untilDestroyed(this),
            map(([isLoggedIn, page, user]) => {
              (event as EddlPageMergedAbstract).collectToken = cart.guid;
              (event as EddlPageMergedAbstract).collectState = EddlCollectionState.START;
              this.lastPageEvent = event;
              return this.fillData(event, undefined, isLoggedIn, page, user);
            })
          )
        )
      );
    }

    return this.hasBeenInitializedSubject.pipe(
      untilDestroyed(this),
      delay(1000),
      distinctUntilChanged(),
      filter(Boolean),
      filter(() => this.documentObject.defaultView?.location?.pathname?.split('/')?.slice(-1)?.[0] !== 'cart'),
      first(),
      switchMap(() =>
        combineLatest([this.authService.isUserLoggedIn(), this.pageLayoutService.page$, this.userFacade.get()]).pipe(
          first(),
          distinctUntilChanged(),
          untilDestroyed(this),
          map(([isLoggedIn, page, user]) => {
            if (event instanceof ProductDetailsPageEvent) {
              (event as EddlPageMergedAbstract).collectToken = user?.uid ?? event?.code;
              (event as EddlPageMergedAbstract).collectState = EddlCollectionState.FINISH;
            }

            this.lastPageEvent = event;
            const data = this.fillData(event, undefined, isLoggedIn, page, user);

            if (event instanceof SearchPageEvent) {
              const searchKey = `search:${this.searchPageEventService.getSearchTerm(event.navigation.url)}`;
              sessionStorage.setItem(EDDL_SEARCH_PAGEVIEW_CONFIRMED_KEY, searchKey);
            }

            return data;
          })
        )
      )
    );
  }

  protected routerSubscribe() {
    this.router.events.pipe(untilDestroyed(this)).subscribe((event) => {
      if (event instanceof NavigationStart) {
        if (event.id !== 1) {
          sessionStorage.setItem('prevLocation', window.location.href);
        }

        // Reset checkout beacon state on navigation
        this.dispatchService.resetCheckoutBeacon();

        // On checkout pages where checkoutStart is expected:
        // - /checkout/data-entry (netnew - first page)
        // - /checkout/review-order from external link (upsell - first page)
        // We need to start collection mode so both checkoutStart and pageView are collected
        // regardless of which fires first.

        // review-order when navigating FROM data-entry will have checkoutStart
        // already fired, so collection mode will NOT be started (isFirstCheckoutPage = false)
        const isFirstCheckoutPage = this.isFirstCheckoutPageInSession(event.url);
        if (isFirstCheckoutPage) {
          this.dispatchService.startCheckoutBeaconCollection();
        }
      }
    });
  }

  /**
   * Determines if this is the first checkout page in the session.
   * - data-entry is always first (netnew flow starts here)
   * - review-order is first only if NOT coming from data-entry (upsell flow)
   */
  private isFirstCheckoutPageInSession(url: string): boolean {
    if (!url?.includes('/checkout/')) {
      return false;
    }

    // data-entry is always the first checkout page for netnew
    if (url?.includes('/checkout/data-entry')) {
      return true;
    }

    // review-order is first only if coming from outside checkout (upsell case)
    // Check if previous location was NOT a checkout page
    if (url?.includes('/checkout/review-order')) {
      const prevLocation = sessionStorage.getItem('prevLocation') || '';
      const wasOnCheckoutPage = prevLocation?.includes('/checkout/');
      return !wasOnCheckoutPage;
    }

    return false;
  }

  protected filterByUrl(event: PageEvent) {
    return event && event.navigation.url !== this.lastPageEvent?.navigation.url;
  }

  protected getNameFromUrl(url: string): string {
    if (typeof url !== 'string') {
      throw new TypeError(`Expected a string for 'url', but got ${typeof url}`);
    }

    const pathWithoutQuery = url.split('?')[0];
    const pathSegments = pathWithoutQuery.split('/').filter(Boolean).slice(2);

    if (!pathSegments || pathSegments.length === 0) {
      return SECTION_HOMEPAGE;
    }

    // Standardize URLs for cartEdit pageView
    // Pattern: /configure/vc/cartEntry/entityKey/* -> /configure/vc/cartEntry
    if (
      pathSegments.length >= 4 &&
      pathSegments[0] === 'configure' &&
      pathSegments[1] === 'vc' &&
      pathSegments[2] === 'cartEntry' &&
      pathSegments[3] === 'entityKey'
    ) {
      return '/configure/vc/cartEntry';
    }

    // Normalize all search-route variants (/search, /search/:term, /search/:term?...) to /search.
    if (pathSegments[0] === 'search') {
      return '/search';
    }

    // For all other routes, return the full path
    const relativePath = pathSegments.join('/');
    return relativePath ? `/${relativePath}` : SECTION_HOMEPAGE;
  }
}
